import React, { useState, useEffect, useRef } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import QRCode from 'qrcode';
import jsPDF from 'jspdf';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Business card dimensions (standard: 3.5" x 2" = 1050px x 600px at 300dpi, we use 2x for high quality)
const CARD_WIDTH = 1050;
const CARD_HEIGHT = 600;
const PREVIEW_SCALE = 0.4; // Scale for preview display

// Aureus Africa brand colors
const COLORS = {
  gold: '#d4af37',
  goldLight: '#f5d78e',
  goldDark: '#b8962d',
  darkBg: '#1a1a1a',
  darkBgSecondary: '#252525',
  textLight: '#ffffff',
  textMuted: '#9ca3af',
  accent: '#c39a3f'
};

interface BusinessCardData {
  fullName: string;
  title: string;
  email: string;
  phone: string;
  phone2: string;
  location: string;
  username: string;
  affiliateUrl: string;
}

interface BusinessCardGeneratorProps {
  userId: number;
  className?: string;
}

interface UserInfo {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  phone_number?: string;
  country?: string;
  city?: string;
}

export const BusinessCardGenerator: React.FC<BusinessCardGeneratorProps> = ({ userId: rawUserId, className = '' }) => {
  const userId = typeof rawUserId === 'string' ? parseInt(rawUserId, 10) : Number(rawUserId);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [showCustomization, setShowCustomization] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [_savedCardId, setSavedCardId] = useState<string | null>(null);

  // Customizable fields
  const [cardData, setCardData] = useState<BusinessCardData>({
    fullName: '',
    title: 'Shareholder Partner',
    email: '',
    phone: '',
    phone2: '',
    location: '',
    username: '',
    affiliateUrl: ''
  });

  useEffect(() => {
    loadUserInfo();
  }, [userId]);

  useEffect(() => {
    if (user) {
      generatePreview();
    }
  }, [cardData, user]);

  const loadUserInfo = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/user/affiliate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'load_business_card'
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      const { savedCard, userData } = result.data;

      if (savedCard) {
        // Use saved business card data
        setSavedCardId(savedCard.id);
        setCardData({
          fullName: savedCard.full_name,
          title: savedCard.title || 'Shareholder Partner',
          email: savedCard.email || '',
          phone: savedCard.phone || '',
          phone2: savedCard.phone2 || '',
          location: savedCard.location || '',
          username: savedCard.username,
          affiliateUrl: savedCard.affiliate_url
        });
        setUser(userData);
      } else if (userData) {
        // Load from user profile
        setUser(userData);

        // Initialize card data from user profile
        const fullName = userData.full_name ||
          [userData.first_name, userData.last_name].filter(Boolean).join(' ') ||
          userData.username || 'Your Name';

        setCardData({
          fullName,
          title: 'Shareholder Partner',
          email: userData.email || '',
          phone: userData.phone_number || '',
          phone2: '',
          location: [userData.city, userData.country].filter(Boolean).join(', ') || '',
          username: userData.username || `user${userData.id}`,
          affiliateUrl: `https://aureus.africa/${userData.username || `user${userData.id}`}`
        });
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate QR code with high error correction for reliable scanning
  const generateQRCode = async (url: string, size: number = 200): Promise<string> => {
    try {
      const qrDataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'H', // High error correction
        margin: 2,
        width: size,
        color: {
          dark: '#1a1a1a', // Dark color for QR modules
          light: '#ffffff' // White background for better scanning
        }
      });
      return qrDataUrl;
    } catch (error) {
      console.error('QR code generation failed:', error);
      return '';
    }
  };

  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  // Draw decorative gold stripe
  const drawGoldStripe = (ctx: CanvasRenderingContext2D) => {
    const stripeY = 180;
    const stripeHeight = 12;

    // Create gradient for gold stripe
    const gradient = ctx.createLinearGradient(0, stripeY, CARD_WIDTH, stripeY);
    gradient.addColorStop(0, COLORS.goldDark);
    gradient.addColorStop(0.3, COLORS.gold);
    gradient.addColorStop(0.5, COLORS.goldLight);
    gradient.addColorStop(0.7, COLORS.gold);
    gradient.addColorStop(1, COLORS.goldDark);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, stripeY, CARD_WIDTH, stripeHeight);
  };

  // Logo URL
  const LOGO_URL = 'https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-m.png';

  // Main card rendering function
  const renderCard = async (ctx: CanvasRenderingContext2D): Promise<void> => {
    // Clear and fill background
    ctx.fillStyle = COLORS.darkBg;
    ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

    // Add subtle gradient overlay
    const bgGradient = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
    bgGradient.addColorStop(0, 'rgba(212, 175, 55, 0.05)');
    bgGradient.addColorStop(0.5, 'transparent');
    bgGradient.addColorStop(1, 'rgba(212, 175, 55, 0.03)');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

    // Draw gold stripe
    drawGoldStripe(ctx);

    // Draw Aureus Africa logo
    const logoSize = 80;
    try {
      const logoImg = await loadImage(LOGO_URL);
      ctx.drawImage(logoImg, 60, 40, logoSize, logoSize);
      // Draw company name next to logo
      ctx.font = 'bold 36px Georgia, serif';
      ctx.fillStyle = COLORS.gold;
      ctx.textAlign = 'left';
      ctx.fillText('AUREUS AFRICA', 60 + logoSize + 15, 90);
    } catch (e) {
      // Fallback to text only if logo fails to load
      ctx.font = 'bold 48px Georgia, serif';
      ctx.fillStyle = COLORS.gold;
      ctx.textAlign = 'left';
      ctx.fillText('AUREUS AFRICA', 60, 100);
    }

    // Tagline
    ctx.font = '18px Arial, sans-serif';
    ctx.fillStyle = COLORS.textMuted;
    ctx.fillText('Gold Mining Share Opportunity', 60, 140);

    // User name (large, prominent)
    ctx.font = 'bold 36px Georgia, serif';
    ctx.fillStyle = COLORS.textLight;
    ctx.fillText(cardData.fullName, 60, 280);

    // Title
    ctx.font = '22px Arial, sans-serif';
    ctx.fillStyle = COLORS.gold;
    ctx.fillText(cardData.title, 60, 320);

    // Contact details section
    const contactStartY = 380;
    const lineHeight = 32;
    ctx.font = '18px Arial, sans-serif';
    ctx.fillStyle = COLORS.textLight;
    let currentLine = 0;

    // Email
    if (cardData.email) {
      ctx.fillStyle = COLORS.textMuted;
      ctx.fillText('✉', 60, contactStartY + lineHeight * currentLine);
      ctx.fillStyle = COLORS.textLight;
      ctx.fillText(cardData.email, 90, contactStartY + lineHeight * currentLine);
      currentLine++;
    }

    // Primary Phone
    if (cardData.phone) {
      ctx.fillStyle = COLORS.textMuted;
      ctx.fillText('📞', 60, contactStartY + lineHeight * currentLine);
      ctx.fillStyle = COLORS.textLight;
      ctx.fillText(cardData.phone, 90, contactStartY + lineHeight * currentLine);
      currentLine++;
    }

    // Secondary Phone
    if (cardData.phone2) {
      ctx.fillStyle = COLORS.textMuted;
      ctx.fillText('📱', 60, contactStartY + lineHeight * currentLine);
      ctx.fillStyle = COLORS.textLight;
      ctx.fillText(cardData.phone2, 90, contactStartY + lineHeight * currentLine);
      currentLine++;
    }

    // Location
    if (cardData.location) {
      ctx.fillStyle = COLORS.textMuted;
      ctx.fillText('📍', 60, contactStartY + lineHeight * currentLine);
      ctx.fillStyle = COLORS.textLight;
      ctx.fillText(cardData.location, 90, contactStartY + lineHeight * currentLine);
    }

    // Website URL at bottom
    ctx.font = '16px Arial, sans-serif';
    ctx.fillStyle = COLORS.gold;
    ctx.fillText(cardData.affiliateUrl, 60, CARD_HEIGHT - 40);

    // QR Code section (right side)
    const qrSize = 200;
    const qrX = CARD_WIDTH - qrSize - 80;
    const qrY = 250;

    // QR code background
    ctx.fillStyle = COLORS.darkBgSecondary;
    ctx.beginPath();
    ctx.roundRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 80, 12);
    ctx.fill();

    // Generate and draw QR code
    const qrDataUrl = await generateQRCode(cardData.affiliateUrl, qrSize);
    if (qrDataUrl) {
      try {
        const qrImg = await loadImage(qrDataUrl);
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
      } catch (e) {
        console.error('Failed to draw QR code:', e);
      }
    }

    // QR code label
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = COLORS.textMuted;
    ctx.textAlign = 'center';
    ctx.fillText('Scan to buy shares', qrX + qrSize / 2, qrY + qrSize + 30);
    ctx.textAlign = 'left';

    // Gold corner accents
    ctx.strokeStyle = COLORS.gold;
    ctx.lineWidth = 3;

    // Top-left corner
    ctx.beginPath();
    ctx.moveTo(20, 50);
    ctx.lineTo(20, 20);
    ctx.lineTo(50, 20);
    ctx.stroke();

    // Bottom-right corner
    ctx.beginPath();
    ctx.moveTo(CARD_WIDTH - 20, CARD_HEIGHT - 50);
    ctx.lineTo(CARD_WIDTH - 20, CARD_HEIGHT - 20);
    ctx.lineTo(CARD_WIDTH - 50, CARD_HEIGHT - 20);
    ctx.stroke();
  };

  const generatePreview = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = CARD_WIDTH;
    canvas.height = CARD_HEIGHT;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    await renderCard(ctx);
    setPreviewUrl(canvas.toDataURL('image/png'));
  };

  // Download as PNG
  const downloadPNG = async () => {
    setGenerating(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = CARD_WIDTH;
      canvas.height = CARD_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      await renderCard(ctx);

      const link = document.createElement('a');
      link.download = `aureus-business-card-${cardData.username}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
    } catch (error) {
      console.error('PNG download failed:', error);
    } finally {
      setGenerating(false);
    }
  };

  // Download as PDF (print-ready)
  const downloadPDF = async () => {
    setGenerating(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = CARD_WIDTH;
      canvas.height = CARD_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      await renderCard(ctx);

      // Create PDF with business card dimensions (3.5" x 2")
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'in',
        format: [3.5, 2]
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      pdf.addImage(imgData, 'PNG', 0, 0, 3.5, 2);
      pdf.save(`aureus-business-card-${cardData.username}.pdf`);
    } catch (error) {
      console.error('PDF download failed:', error);
    } finally {
      setGenerating(false);
    }
  };

  // Download multiple cards for printing (8 cards per A4 page)
  const downloadPrintSheet = async () => {
    setGenerating(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = CARD_WIDTH;
      canvas.height = CARD_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      await renderCard(ctx);
      const cardImgData = canvas.toDataURL('image/png', 1.0);

      // A4 size in inches: 8.27 x 11.69
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'a4'
      });

      // Card dimensions in inches
      const cardW = 3.5;
      const cardH = 2;
      const margin = 0.25;

      // 2 columns x 4 rows = 8 cards per page
      const positions = [
        { x: margin, y: margin },
        { x: margin + cardW + margin, y: margin },
        { x: margin, y: margin + cardH + margin },
        { x: margin + cardW + margin, y: margin + cardH + margin },
        { x: margin, y: margin + (cardH + margin) * 2 },
        { x: margin + cardW + margin, y: margin + (cardH + margin) * 2 },
        { x: margin, y: margin + (cardH + margin) * 3 },
        { x: margin + cardW + margin, y: margin + (cardH + margin) * 3 }
      ];

      positions.forEach(pos => {
        pdf.addImage(cardImgData, 'PNG', pos.x, pos.y, cardW, cardH);
      });

      // Add cut lines
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.01);
      positions.forEach(pos => {
        pdf.rect(pos.x, pos.y, cardW, cardH);
      });

      pdf.save(`aureus-business-cards-print-sheet-${cardData.username}.pdf`);
    } catch (error) {
      console.error('Print sheet download failed:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleInputChange = (field: keyof BusinessCardData, value: string) => {
    setCardData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'username' ? { affiliateUrl: `https://aureus.africa/${value}` } : {})
    }));
    setHasUnsavedChanges(true);
  };

  // Save business card data to database
  const saveBusinessCard = async () => {
    try {
      setSaving(true);
      setSaveMessage(null);

      const cardRecord = {
        full_name: cardData.fullName,
        title: cardData.title,
        email: cardData.email,
        phone: cardData.phone,
        phone2: cardData.phone2,
        location: cardData.location,
        username: cardData.username,
        affiliate_url: cardData.affiliateUrl,
        is_default: true
      };

      const response = await fetch(`${API_BASE_URL}/api/user/affiliate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'save_business_card',
          data: { cardData: cardRecord }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      setHasUnsavedChanges(false);
      setSaveMessage({ type: 'success', text: 'Business card saved successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving business card:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save business card. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Reset to profile defaults
  const resetToDefaults = async () => {
    if (!user) return;

    const fullName = user.full_name ||
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      user.username || 'Your Name';

    setCardData({
      fullName,
      title: 'Shareholder Partner',
      email: user.email || '',
      phone: user.phone_number || '',
      phone2: '',
      location: [user.city, user.country].filter(Boolean).join(', ') || '',
      username: user.username || `user${user.id}`,
      affiliateUrl: `https://aureus.africa/${user.username || `user${user.id}`}`
    });
    setHasUnsavedChanges(true);
  };

  if (loading) {
    return (
      <div className={`bg-gray-800 rounded-lg border border-gray-700 p-6 ${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-3 text-white">Loading business card generator...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-800 rounded-lg border border-gray-700 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <span>💳</span> Business Card Generator
          </h2>
          <p className="text-white text-sm mt-1">
            Create professional business cards with your referral QR code
          </p>
        </div>
        <button
          onClick={() => setShowCustomization(!showCustomization)}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
        >
          {showCustomization ? '✕ Close' : '⚙️ Customize'}
        </button>
      </div>

      {/* Customization Panel */}
      {showCustomization && (
        <div className="bg-gray-900 rounded-lg p-4 mb-6 border border-gray-700">
          <h3 className="text-white font-semibold mb-4">Customize Your Card</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white text-sm mb-1">Full Name</label>
              <input
                type="text"
                value={cardData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-500 focus:outline-none"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-white text-sm mb-1">Title</label>
              <input
                type="text"
                value={cardData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-500 focus:outline-none"
                placeholder="Shareholder Partner"
              />
            </div>
            <div>
              <label className="block text-white text-sm mb-1">Email</label>
              <input
                type="email"
                value={cardData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-500 focus:outline-none"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-white text-sm mb-1">Primary Phone</label>
              <input
                type="tel"
                value={cardData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-500 focus:outline-none"
                placeholder="+1 234 567 8900"
              />
            </div>
            <div>
              <label className="block text-white text-sm mb-1">Secondary Phone (Optional)</label>
              <input
                type="tel"
                value={cardData.phone2}
                onChange={(e) => handleInputChange('phone2', e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-500 focus:outline-none"
                placeholder="+1 234 567 8901"
              />
            </div>
            <div>
              <label className="block text-white text-sm mb-1">Location</label>
              <input
                type="text"
                value={cardData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-500 focus:outline-none"
                placeholder="City, Country"
              />
            </div>
            <div>
              <label className="block text-white text-sm mb-1">Username (for URL)</label>
              <input
                type="text"
                value={cardData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-yellow-500 focus:outline-none"
                placeholder="username"
              />
            </div>
          </div>
          <div className="mt-4 p-3 bg-gray-800 rounded-lg">
            <span className="text-white text-sm">Your referral URL: </span>
            <span className="text-yellow-400 font-mono text-sm">{cardData.affiliateUrl}</span>
          </div>

          {/* Save/Reset Buttons */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button
              onClick={saveBusinessCard}
              disabled={saving || !hasUnsavedChanges}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                hasUnsavedChanges
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  💾 {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
                </>
              )}
            </button>
            <button
              onClick={resetToDefaults}
              className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            >
              🔄 Reset to Profile
            </button>
          </div>

          {/* Save Status Message */}
          {saveMessage && (
            <div className={`mt-3 p-3 rounded-lg text-center ${
              saveMessage.type === 'success'
                ? 'bg-green-900/30 border border-green-500/30 text-green-400'
                : 'bg-red-900/30 border border-red-500/30 text-red-400'
            }`}>
              {saveMessage.type === 'success' ? '✅' : '❌'} {saveMessage.text}
            </div>
          )}

          {/* Unsaved Changes Indicator */}
          {hasUnsavedChanges && (
            <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-500/30 rounded-lg text-center">
              <span className="text-yellow-400 text-sm">⚠️ You have unsaved changes</span>
            </div>
          )}
        </div>
      )}

      {/* Card Preview */}
      <div className="mb-6">
        <h3 className="text-white font-semibold mb-3">Preview</h3>
        <div className="flex justify-center bg-gray-900 rounded-lg p-6 border border-gray-700">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Business Card Preview"
              style={{
                width: CARD_WIDTH * PREVIEW_SCALE,
                height: CARD_HEIGHT * PREVIEW_SCALE,
                borderRadius: '8px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
              }}
            />
          ) : (
            <div
              className="flex items-center justify-center bg-gray-800 rounded-lg"
              style={{ width: CARD_WIDTH * PREVIEW_SCALE, height: CARD_HEIGHT * PREVIEW_SCALE }}
            >
              <span className="text-gray-500">Generating preview...</span>
            </div>
          )}
        </div>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>

      {/* Download Options */}
      <div className="space-y-4">
        <h3 className="text-white font-semibold">Download Options</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* PNG Download */}
          <button
            onClick={downloadPNG}
            disabled={generating}
            className="flex flex-col items-center p-4 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-3xl mb-2">🖼️</span>
            <span className="text-white font-semibold">Download PNG</span>
            <span className="text-blue-200 text-sm mt-1">High-quality image</span>
          </button>

          {/* PDF Download */}
          <button
            onClick={downloadPDF}
            disabled={generating}
            className="flex flex-col items-center p-4 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-3xl mb-2">📄</span>
            <span className="text-white font-semibold">Download PDF</span>
            <span className="text-red-200 text-sm mt-1">Print-ready format</span>
          </button>

          {/* Print Sheet */}
          <button
            onClick={downloadPrintSheet}
            disabled={generating}
            className="flex flex-col items-center p-4 bg-gradient-to-br from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-3xl mb-2">🖨️</span>
            <span className="text-white font-semibold">Print Sheet (8 cards)</span>
            <span className="text-yellow-200 text-sm mt-1">A4 page with cut lines</span>
          </button>
        </div>

        {generating && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
            <span className="ml-3 text-white">Generating your business card...</span>
          </div>
        )}
      </div>

      {/* Tips Section */}
      <div className="mt-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
        <h4 className="text-yellow-400 font-semibold mb-2">💡 Pro Tips</h4>
        <ul className="text-white text-sm space-y-1">
          <li>• Use the <strong>Print Sheet</strong> option to print 8 cards on a single A4 page</li>
          <li>• The QR code links directly to your personal landing page</li>
          <li>• For best quality, use a professional printing service with the PDF file</li>
          <li>• Standard business card size: 3.5" × 2" (89mm × 51mm)</li>
        </ul>
      </div>
    </div>
  );
};

export default BusinessCardGenerator;

